/* ============================================================
   knobs-instantsearch.js — InstantSearch.js-powered /search

   Replaces assets/knobs-algolia-search.js (the bespoke 36KB
   takeover with hand-rolled URL state, facet rendering, and
   event binding). Built on InstantSearch.js v4.

   Design doc: Obsidian/Ecommerce/Knobs Migration/InstantSearch.js Rebuild — Design Doc.md
   Status: STAGING ONLY (theme 188907684119). Reviewed/approved
   before live deploy.

   Key decisions reflected here:
   - Option B for facets: client-side tag-prefix parsing of the
     single `tags` attribute (one widget, multi-section render)
   - Vendor for Brand (NOT the "Brand:" tag prefix — that's skipped)
   - SKIP_PREFIXES is complete (fixes the "duplicate Brand" bug)
   - Auto-apply on click (no "Apply Filters" button)
   - Insights events wired (click, addedToCart, conversion)
   - Failover: window.tkAlgoliaActive set only after first render
   - Recommended/Best Selling sort live now; Price asc/desc when
     replicas exist
   ============================================================ */

(function () {
  'use strict';

  // This integration powers BOTH /search and collection pages. On collection
  // pages it only activates when the template renders the #knobs-coll-algolia
  // marker (carrying the collection id), and it scopes results to that
  // collection via collection_ids (see configure widget below). All
  // collection-specific logic is gated on KIS_IS_COLLECTION so /search
  // behavior is unchanged.
  var KIS_IS_SEARCH = /^\/search(\?|$|\/)/.test(location.pathname + location.search);
  var KIS_COLL_MARKER = document.getElementById('knobs-coll-algolia');
  var KIS_IS_COLLECTION = /^\/collections\//.test(location.pathname) && !!KIS_COLL_MARKER;
  if (!KIS_IS_SEARCH && !KIS_IS_COLLECTION) return;
  var KIS_COLLECTION_ID = KIS_COLL_MARKER ? KIS_COLL_MARKER.getAttribute('data-collection-id') : null;

  /* ───────── Mobile filters toggle ─────────
     Independent of InstantSearch — wires the .knobs-srch__filter-toggle
     button to show/hide the sidebar at ≤768px. Runs as soon as the DOM
     has the elements; safe to bind early. */
  function bindFilterToggle() {
    var btn = document.querySelector('.knobs-srch__filter-toggle');
    var sidebar = document.getElementById('knobs-srch-sidebar');
    if (!btn || !sidebar || btn.__bound) return;
    btn.__bound = true;
    btn.addEventListener('click', function () {
      var open = sidebar.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindFilterToggle);
  } else {
    bindFilterToggle();
  }

  // Module-level flag so the global timeout below can observe first-render success.
  var renderedOnce = false;

  // Active Finish-Class refinement (e.g. "nickel"), used to switch card images
  // for products that have a finish_class_image map. Updated by the render hook
  // whenever refinements change; read by renderHit.
  var currentFinishClass = null;

  // Failover: if InstantSearch never renders within 10 seconds (Algolia unreachable,
  // libs blocked by an extension, malformed bundle), swap the page into a graceful
  // fallback state so the user can recover instead of staring at "Loading filters…".
  setTimeout(function () {
    if (renderedOnce) return;
    var hits = document.getElementById('is-hits');
    var stats = document.getElementById('is-stats');
    var fallback = document.getElementById('is-loading-fallback');
    if (stats && !stats.textContent.trim()) {
      stats.innerHTML = 'Search is temporarily unavailable.';
    }
    if (fallback) {
      fallback.innerHTML = 'Filters unavailable. <a href="/collections/all">Browse all products</a> or refresh the page.';
    }
    if (hits && !hits.innerHTML.trim()) {
      hits.innerHTML = '<p class="knobs-srch__empty">Search is unavailable right now. <a href="/collections/all">Browse all products</a> or refresh the page.</p>';
    }
    console.warn('[knobs-is] timeout — first render did not happen within 10s');
  }, 10000);

  const APP_ID = 'YQLK7JGQ6E';
  const SEARCH_KEY = '11defc1b66b77421e9de1469f21f250d';
  // ── Typesense = primary engine. Algolia client stays as fail-open fallback
  //    until the Algolia account is closed; then the catch falls to SSR.
  const TS_HOST = '8dcxftm9ab6hl0s4p-1.a2.typesense.net';
  const TS_SEARCH_KEY = 'uaK8IihBH8YEqaZPgyLVG1GsBNsOlcpJ'; // search-only scope (public, like Algolia's)
  // body_html_safe is EXCLUDED from query_by: it made group_by queries with 4+
  // tokens pathologically slow (22s -> 47ms, verified 2026-07-09) AND let
  // description text hijack category intent (backplates ranking for 'knobs and
  // pulls'). Descriptions are still stored/displayed, just not searched.
  const TS_QUERY_BY = 'product_type,title,sku,upc,barcode,vendor,finish,tags,named_tags_names,context_words';
  // context_words (LAST = weakest weight): curated placement words — drawer/dresser/
  // kitchen/towel… — from context-words.json via the reseed. Replaces what description
  // matching gave Algolia, without the body_html_safe cost (2026-07-09 incident).
  // Adapter convention: "collection/sort/<sort_by>" switches sort like an Algolia replica.
  const INDEX_PRIMARY = 'products';
  const INDEX_BEST_SELLING = 'products/sort/recently_ordered_count:desc,boost_score:desc';
  const INDEX_PRICE_ASC = 'products/sort/price:asc';
  const INDEX_PRICE_DESC = 'products/sort/price:desc';
  const INDEX_TITLE_ASC = 'products/sort/title:asc';
  const INDEX_TITLE_DESC = 'products/sort/title:desc';
  // Algolia fallback needs the old replica names:
  const ALG_INDEX_MAP = {
    'products': 'shopify_products',
    'products/sort/recently_ordered_count:desc,boost_score:desc': 'shopify_products_recently_ordered_count_desc',
    'products/sort/price:asc': 'shopify_products_price_asc',
    'products/sort/price:desc': 'shopify_products_price_desc',
    'products/sort/title:asc': 'shopify_products_title_asc',
    'products/sort/title:desc': 'shopify_products_title_desc',
  };

  /* ───────── Brand merchandising (collection browse) ─────────
     BRAND_WEIGHTS devalues weak brands in the BRAND FILTER order: the facet
     sorts by count × weight, so a high-count weak brand drops without flipping
     everyone else off count-order. 1.0 = unchanged. Tune freely.
     PAGE_BRAND_CAP caps how many of one brand appear in the front of a
     collection page, so one brand can't wall page 1 (collections only —
     /search is query-relevance and left alone). Cheap: just array ops. */
  var BRAND_WEIGHTS = { 'alno': 0.4, 'belwith': 0.4, 'anne at home': 0.4 };
  function brandWeight(v) { return BRAND_WEIGHTS[(v || '').toLowerCase().trim()] || 1; }
  var PAGE_BRAND_CAP = 8;

  /* ───────── Page size ─────────
     MUST be sent explicitly. Algolia carried this in the index settings
     (hitsPerPage: 20) and applied it server-side whenever InstantSearch didn't
     ask; Typesense has no index-level equivalent, and its adapter only forwards
     per_page when hitsPerPage is set — so omitting it silently falls back to
     Typesense's server default of 10. 24 matches the SSR collection grid's
     target_visible (knobs-collection.liquid: "4x6 desktop grid") and divides
     evenly by every desktop column count (4/3/2/1), so pages never break on a
     partial row. An earlier 40/page (to give PAGE_BRAND_CAP room to diversify)
     ~doubled the product images and slowed the page — don't go back there. */
  var HITS_PER_PAGE = 24;

  // Round-robin interleave by vendor so the front of the page alternates brands
  // (TK, JA, TK, JA…) — up to `cap` per brand — then the remainder follows in
  // rank order. No products dropped (no loss / no dup); just reordered in-page.
  function capByVendor(items, cap) {
    var groups = {}, order = [];
    items.forEach(function (h) {
      var v = (h.vendor || '').toLowerCase();
      if (!groups[v]) { groups[v] = []; order.push(v); }
      groups[v].push(h);
    });
    var front = [], rest = [];
    for (var round = 0; round < cap; round++) {
      for (var i = 0; i < order.length; i++) {
        var q = groups[order[i]];
        if (round < q.length) front.push(q[round]);
      }
    }
    for (var j = 0; j < order.length; j++) {
      var g = groups[order[j]];
      for (var k = cap; k < g.length; k++) rest.push(g[k]);
    }
    return front.concat(rest);
  }

  // Wait for libraries (loaded via CDN <script defer>)
  function libsReady() {
    return typeof window.algoliasearch !== 'undefined' &&
           typeof window.instantsearch !== 'undefined' &&
           typeof window.TypesenseInstantSearchAdapter !== 'undefined';
  }

  function whenReady(cb, tries) {
    tries = tries || 0;
    if (libsReady()) return cb();
    if (tries > 200) { console.warn('[knobs-is] libs never loaded; falling back to SSR'); return; }
    setTimeout(function () { whenReady(cb, tries + 1); }, 50);
  }

  /* ───────── Tag-axis grouping (mirrors knobs-algolia-search.js logic, FIXED) ───────── */

  // Prefixes to suppress from the sidebar (their data lives in another facet
  // or doesn't make sense as a user-facing filter).
  //   - Brand: covered by vendor facet (THIS IS THE FIX for the duplicate-Brand bug)
  //   - primary-finish: internal flag
  //   - Country of Origin: not a real shopping axis
  //   - Price-Range: duplicates the price slider
  //   - Product-Flag: New Arrival / Best Seller — for display only, not filtering
  const SKIP_PREFIXES = /^(Brand|primary-finish|Country of Origin|Country Of Origin|Price-Range|Product-Flag)$/i;

  // Ordered top of the sidebar; anything not listed comes alphabetically after.
  const GROUP_ORDER = ['Style', 'Finish-Class', 'Drill-Centers-Range', 'Drill-Centers', 'Collection', 'Material'];

  function displayGroupLabel(prefix) {
    if (prefix === 'Finish-Class') return 'Finish';
    return prefix.replace(/-/g, ' ');
  }

  function groupTagItems(items) {
    var groups = {};
    items.forEach(function (item) {
      var label = item.label || item.value || '';
      var colon = label.indexOf(':');
      if (colon <= 0) return;
      var prefix = label.slice(0, colon).trim();
      var value = label.slice(colon + 1).trim();
      if (SKIP_PREFIXES.test(prefix)) return;
      if (!groups[prefix]) groups[prefix] = [];
      groups[prefix].push(Object.assign({}, item, { displayValue: value }));
    });
    return groups;
  }

  function orderedGroupKeys(groups) {
    var inOrder = GROUP_ORDER.filter(function (k) { return groups[k] && groups[k].length; });
    var rest = Object.keys(groups).filter(function (k) {
      return GROUP_ORDER.indexOf(k) < 0 && groups[k] && groups[k].length;
    }).sort();
    return inOrder.concat(rest);
  }

  /* ───────── Specific Finish drill-down (restored 2026-06-11) ─────────
     The pre-IS sidebar had a "Specific Finish" group on finish pages
     (Kayla/Dave 2026-05-20): pick a family (Bronze) → choose the exact
     finish (Honey / Champagne / Satin Bronze …). The IS takeover lost it.
     Now a refinement list on `meta.details.finish` (variant-accurate),
     shown only when a Finish family is active. Family tags are
     PRODUCT-level (a multi-finish product's nickel variant still carries
     "Finish-Class: Bronze"), so the value list must be family-filtered
     client-side via keyword classification or sister-finish noise leaks in. */

  var FINISH_FAMILY_KEYWORDS = ['aluminum', 'black', 'brass', 'bronze', 'brown', 'chrome', 'clear', 'copper', 'gold', 'gray', 'grey', 'iron', 'nickel', 'pewter', 'silver', 'steel', 'white'];

  function familiesOfFinish(name) {
    var s = String(name || '').toLowerCase();
    var fams = [];
    FINISH_FAMILY_KEYWORDS.forEach(function (f) {
      if (s.indexOf(f) >= 0) fams.push(f === 'grey' ? 'gray' : f);
    });
    return fams;
  }

  // Active finish families = checked Finish values (Finish-Class: tags) plus,
  // on /collections/finish-<family> pages, the family from the handle.
  function activeFinishClasses(renderState) {
    var classes = [];
    try {
      var helper = renderState.instantSearchInstance && renderState.instantSearchInstance.helper;
      var refs = helper && helper.state && helper.state.disjunctiveFacetsRefinements;
      var tags = (refs && refs.tags) || [];
      tags.forEach(function (t) {
        var m = /^Finish-Class:\s*(.+)$/i.exec(String(t));
        if (m) classes.push(m[1].trim().toLowerCase());
      });
    } catch (e) { /* state not ready — handle-derived class below still applies */ }
    if (KIS_IS_COLLECTION) {
      var m2 = /^\/collections\/finish-([a-z]+)\/?$/.exec(location.pathname);
      if (m2 && FINISH_FAMILY_KEYWORDS.indexOf(m2[1]) >= 0 && classes.indexOf(m2[1]) < 0) {
        classes.push(m2[1] === 'grey' ? 'gray' : m2[1]);
      }
    }
    return classes;
  }

  // The group's <details> lives in the section markup; the tag-axis renderer
  // detaches/re-inserts it under the Finish group each render, so keep a
  // reference — getElementById can't find it while detached.
  var _sfGroupEl;
  function sfGroupEl() {
    if (_sfGroupEl === undefined) _sfGroupEl = document.getElementById('is-specific-finish-group');
    return _sfGroupEl;
  }

  /* ───────── Helpers ───────── */

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function escapeAttr(s) { return escapeHtml(s); }

  // Product cards render ~240-360px wide; the stored image is a full-res original.
  // Ask Shopify to resize on the fly (?width=600 ~= 2x retina) instead of shipping
  // a ~1000px+ file per card — the browse-grid half of the slow-thumbnail fix.
  function sizedImg(url, w) {
    if (!url || url.indexOf('cdn.shopify.com') === -1 || /[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function formatPrice(cents) {
    var n = typeof cents === 'number' ? cents : parseFloat(cents);
    if (isNaN(n)) return '';
    return '$' + (n).toFixed(2).replace(/\.00$/, '');
  }

  /* ───────── Finish color map (lifted from old knobs-algolia-search.js) ───────── */
  var FINISH_HEX = {
    'matte black': '#0d0d0d', 'flat black': '#1d1d1f', 'tuscan bronze': '#7d5a30',
    'honey bronze': '#b48342', 'oil rubbed bronze': '#2a1a0e', 'umbrio': '#1f1814',
    'german bronze': '#5d3530', 'antique bronze': '#5e4a36', 'polished nickel': '#c8c2b0',
    'brushed satin nickel': '#bdb9ad', 'satin nickel': '#bdb9ad', 'pewter': '#8e8e88',
    'pewter antique': '#6f6c64', 'antique pewter': '#6f6c64', 'brushed pewter': '#9a9892',
    'brushed satin brass': '#b89a5a', 'brushed brass': '#b89a5a', 'satin brass': '#c4a868',
    'polished brass': '#d4af37', 'unlacquered brass': '#b89a5a', 'brushed gold': '#cdb380',
    'gold': '#d4af37', 'rose gold': '#b76e79', 'champagne bronze': '#a8895c',
    'polished chrome': '#d8dde0', 'chrome': '#d8dde0', 'brushed chrome': '#b8b8b8',
    'matte chrome': '#a8a8a8', 'polished copper': '#b87333', 'antique copper': '#7e4427',
    'brushed copper': '#9a5e34', 'antique english': '#4a3a2a', 'flat white': '#f5f5f0',
    'matte white': '#f5f5f0', 'white': '#ffffff', 'gunmetal': '#2a3439', 'graphite': '#383838',
    'silver': '#bcbcb6', 'brushed bronze': '#7e6a4f', 'aged bronze': '#5e5132',
    'brushed nickel': '#9c9a93', 'slate': '#525a62', 'venetian bronze': '#6e4326',
    'brushed stainless steel': '#a9a9a8', 'brass': '#b89a5a', 'copper': '#b87333',
    'nickel': '#c8c2b0', 'steel': '#8a8c8e', 'gray': '#888888', 'grey': '#888888',
    'bronze': '#7a5a3a', 'champagne': '#d6c08e', 'matte nickel': '#b0aea4',
    'matte gold': '#c4a85a', 'matte brass': '#ad8a40', 'antique brass': '#8a7644',
    'satin gold': '#c4a558', 'black': '#0a0a0a', 'stainless': '#b8b8b6',
    'stainless steel': '#c0c0bc', 'dark bronze': '#3a2618', 'matte satin nickel': '#b0aea4',
    'black nickel': '#2a2826', 'antique nickel': '#9c958a', 'antique silver': '#a8a59c',
  };

  function finishSwatchStyle(name) {
    var k = (name || '').toLowerCase().trim();
    var hex = FINISH_HEX[k];
    if (!hex) {
      // Try matching common keywords
      if (/black/.test(k)) hex = '#1a1a1a';
      else if (/bronze/.test(k)) hex = '#7a5a3a';
      else if (/brass|gold/.test(k)) hex = '#b89a5a';
      else if (/nickel|silver|chrome|stainless/.test(k)) hex = '#bdb9ad';
      else if (/copper/.test(k)) hex = '#b87333';
      else if (/pewter/.test(k)) hex = '#8e8e88';
      else if (/white/.test(k)) hex = '#f0f0e8';
      else hex = '#9a9a9a'; // neutral fallback
    }
    if (hex.indexOf('gradient') >= 0 || hex.indexOf(',') >= 0) {
      return 'background:' + hex;
    }
    return 'background:' + hex;
  }

  /* ───────── Title cleanup (mirror of the SKU-strip heuristic in knobs-product-card.liquid) ───────── */
  function cleanTitle(title) {
    if (!title) return '';
    var words = title.split(/\s+/);
    if (words.length <= 1) return title;
    var last = words[words.length - 1];
    if (last.length < 4) return title;
    if (last !== last.toUpperCase()) return title;
    if (!/\d/.test(last)) return title;
    if (/['"\/(),%]/.test(last)) return title;
    return words.slice(0, -1).join(' ');
  }

  /* ───────── Insights events (Phase-3 scope per Dave's approval) ───────── */

  function emitInsights(eventName, payload) {
    // Algolia Insights tag the autocomplete app embed should have already
    // installed window.aa. If not present, no-op.
    if (typeof window.aa !== 'function') return;
    try { window.aa(eventName, payload); } catch (e) { /* swallow */ }
  }

  /* ───────── Hit template — matches snippets/knobs-product-card.liquid markup ───────── */

  function renderHit(hit, opts) {
    opts = opts || {};
    var queryID = opts.queryID || '';
    var position = opts.position || 0;

    // Sale badge
    var saleBadge = '';
    if (hit.compare_at_price && hit.compare_at_price > hit.price) {
      var pct = Math.round((1 - (hit.price / hit.compare_at_price)) * 100);
      if (pct >= 5) saleBadge = '<span class="knobs-pcard__sale-badge">Save ' + pct + '%</span>';
    }

    // Image selection — priority order:
    //   1. If a Finish-Class refinement is active AND this product has a
    //      finish_class_image map with that class, use the mapped image.
    //      Pattern B (multi-variant product with one Shopify product per design).
    //   2. Otherwise prefer hit.image (the SKU-specific image) over
    //      hit.product_image (the family's "primary" image). Pattern A products
    //      (one Shopify product per finish SKU like Amerock BP1581WN/WID/R2/ORB)
    //      need this so each card shows its OWN finish, not the family primary.
    //   3. Fall back to product_image.
    var img = '';
    if (opts.activeFinishClass) {
      var fci = (hit.meta && hit.meta.custom && hit.meta.custom.finish_class_image) || null;
      // Algolia may store this as a string (JSON Encoded type) OR as a parsed object
      if (typeof fci === 'string') {
        try { fci = JSON.parse(fci); } catch (e) { fci = null; }
      }
      if (fci && typeof fci === 'object') {
        var key = (opts.activeFinishClass || '').toLowerCase().trim();
        if (fci[key]) img = fci[key];
      }
    }
    if (!img) img = hit.image || hit.product_image || '';
    img = sizedImg(img, 600);
    var imgHtml = img
      ? '<img src="' + escapeAttr(img) + '" class="knobs-pcard__img" alt="' + escapeAttr(hit.title || '') + '" loading="lazy">'
      : '<div class="knobs-pcard__img knobs-pcard__img--placeholder"></div>';

    // Finish swatches — telegraph "this design also comes in N finishes".
    // Primary source = meta.custom.family_finishes (sister SKUs across the design
    // family — the SAME rich source the SSR card uses via top_knobs.family_finishes;
    // now present in the Algolia index as a JSON-encoded string array). Fallback =
    // per-hit Finish-Class tags (covers multi-variant products like Schaub when a
    // family list is absent). Only render for 2+ distinct finishes; a lone dot just
    // repeats what the photo already shows.
    var finishSwatches = '';
    var finishNames = [];
    var ff = (hit.meta && hit.meta.custom && hit.meta.custom.family_finishes) || null;
    if (typeof ff === 'string') { try { ff = JSON.parse(ff); } catch (e) { ff = null; } }
    if (Array.isArray(ff)) {
      finishNames = ff.map(function (n) { return (typeof n === 'string') ? n : (n && n.name) || ''; });
    }
    if (!finishNames.length && Array.isArray(hit.tags)) {
      finishNames = hit.tags
        .filter(function (t) { return /^Finish-Class:\s*/.test(t); })
        .map(function (t) { return t.replace(/^Finish-Class:\s*/, '').trim(); });
    }
    {
      var seen = {};
      var unique = finishNames.filter(function (n) {
        if (!n || seen[n]) return false;
        seen[n] = true;
        return true;
      });
      if (unique.length >= 2) {
        var dots = unique.slice(0, 4).map(function (n) {
          return '<span class="knobs-swatch knobs-pcard__dot" style="' + finishSwatchStyle(n) + '" title="' + escapeAttr(n) + '" aria-label="' + escapeAttr(n) + '"></span>';
        }).join('');
        finishSwatches = '<div class="knobs-pcard__finishes">' + dots;
        if (unique.length > 4) {
          finishSwatches += '<span class="knobs-pcard__more">+' + (unique.length - 4) + '</span>';
        }
        finishSwatches += '</div>';
      }
    }

    // Price (with strikethrough compare_at)
    var priceWas = (hit.compare_at_price && hit.compare_at_price > hit.price)
      ? '<span class="knobs-pcard__price-was">' + formatPrice(hit.compare_at_price) + '</span>'
      : '';

    var title = cleanTitle(hit.title || '');
    var vendor = hit.vendor ? '<p class="knobs-pcard__collection">' + escapeHtml(hit.vendor) + '</p>' : '';
    var skuLine = hit.sku ? '<p class="knobs-pcard__sku">SKU: ' + escapeHtml(hit.sku) + '</p>' : '';

    // Quick Add — mirrors the SSR card's hover button. The index is variant-level
    // (objectID = variant id), so each card is already a specific purchasable SKU;
    // adding that variant id is valid. Guard: real variant id + in stock + priced
    // (skip $0 quote-only "call for price" items, which can't go straight to cart).
    var quickAdd = '';
    if (/^\d+$/.test(String(hit.objectID)) && hit.inventory_available !== false && hit.price > 0) {
      quickAdd = '<div class="knobs-pcard__quick-add" data-variant-id="' + escapeAttr(hit.objectID) + '">' +
                   '<span>Quick Add to Cart</span>' +
                 '</div>';
    }

    return (
      '<div class="knobs-pcard">' +
        '<a class="knobs-pcard__link" href="/products/' + escapeAttr(hit.handle) + (/^\d+$/.test(String(hit.objectID)) ? '?variant=' + encodeURIComponent(hit.objectID) : '') + '" ' +
          'data-object-id="' + escapeAttr(hit.objectID) + '" data-query-id="' + escapeAttr(queryID) + '" ' +
          'data-position="' + position + '">' +
          '<div class="knobs-pcard__media">' +
            imgHtml +
            saleBadge +
            quickAdd +
          '</div>' +
          '<div class="knobs-pcard__info">' +
            vendor +
            '<p class="knobs-pcard__name">' + escapeHtml(title) + '</p>' +
            skuLine +
            '<div class="knobs-pcard__bottom">' +
              '<div class="knobs-pcard__price-wrap">' +
                '<span class="knobs-pcard__price">' + formatPrice(hit.price) + '</span>' +
                priceWas +
              '</div>' +
              finishSwatches +
            '</div>' +
          '</div>' +
        '</a>' +
      '</div>'
    );
  }

  /* ───────── Main init ───────── */

  function startInstantSearch() {
    var instantsearch = window.instantsearch;
    var algoliasearch = window.algoliasearch;
    var connectors = instantsearch.connectors;
    var widgets = instantsearch.widgets;

    // Typesense adapter: translates Algolia-shaped requests (facetFilters,
    // numericFilters, facets, pagination, highlighting) into Typesense
    // multi_search and returns Algolia-shaped responses — so every widget,
    // renderer and URL-routing block below is unchanged.
    var _tsAdapter = new window.TypesenseInstantSearchAdapter({
      server: {
        apiKey: TS_SEARCH_KEY,
        nodes: [{ host: TS_HOST, port: 443, protocol: 'https' }],
        connectionTimeoutSeconds: 4,
        cacheSearchResultsForSeconds: 60,
      },
      // Facet VALUES contain colons ('Style: Modern') — without this list the
      // adapter mis-parses 'tags:Style: Modern' and matches loosely (verified:
      // 74 results where exact = 64). Listing the facet fields pins the split.
      facetableFieldsWithSpecialCharacters: ['tags', 'vendor', 'product_type', 'meta.details.finish', 'finish', 'collections', 'collection_ids', 'price_range', 'option_names', 'family_key'],
      additionalSearchParameters: {
        query_by: TS_QUERY_BY,
        // Server-side stopword set 'knobs-fillers' (= Algolia's optionalWords:
        // in/inch/mm/cm/cc/center/to/for/with/the/on/and/of/a/an). Fixes size
        // queries ('4 inch pull', '2.5" cc', 'back plates for knobs') where the
        // filler token matched nothing. GRID ONLY — autocomplete must NOT get
        // this: stopword-reduced keystroke prefixes ('24 in'->'24') are slow.
        stopwords: 'knobs-fillers',
        // Bucketed relevance: full matches tie, then Catalin's boost decides —
        // the merchandising-first ordering (see BUILD.md "Parity verdict").
        sort_by: '_text_match(buckets:6):desc,boost_score:desc,recently_ordered_count:desc',
        group_by: 'family_key',       // = Algolia distinct on family_key
        group_limit: 1,
        drop_tokens_threshold: 1,     // = removeWordsIfNoResults-style recovery
        prioritize_exact_match: true,
        // Return ONLY the fields the product card reads (incl. meta for finish
        // swatches). Drops body_html_safe/variants_*/options/named_tags etc — cut a
        // 20-hit browse page 98KB -> 47KB (verified 2026-07-09). The grid uses no
        // highlight snippets, so switch highlighting off to save compute + bytes.
        include_fields: 'objectID,id,handle,title,vendor,sku,price,compare_at_price,image,product_image,inventory_available,tags,meta',
        highlight_fields: 'none',
        highlight_full_fields: 'none',
      },
    });
    var _baseClient = _tsAdapter.searchClient;
    var _algoliaFallback = algoliasearch(APP_ID, SEARCH_KEY);

    // ─── Tag-axis AND fix ───────────────────────────────────────────
    // All filter axes (Style, Finish-Class, Drill-Centers, Material, …) live as
    // prefixed values inside the SINGLE `tags` attribute, so InstantSearch's
    // disjunctive (OR) facet on `tags` OR's them ALL together — selecting a
    // drill-center AND a finish returns items matching EITHER (i.e. "everything").
    // We have no per-axis attributes to facet on (options/named_tags are empty),
    // so we regroup the outgoing facetFilters by axis prefix at the client layer:
    // each axis becomes its own AND group, values within an axis stay OR'd.
    // Result set is then correctly AND-across / OR-within. (Disjunctive COUNT
    // queries strip the tags refinement entirely, so per-tag counts are unaffected.)
    function axisOf(tagFilter) {
      // tagFilter like "tags:Finish-Class: Bronze" → "Finish-Class"
      var neg = tagFilter.charAt(0) === '-';
      var body = (neg ? tagFilter.slice(1) : tagFilter);
      if (body.indexOf('tags:') !== 0) return null;
      var rest = body.slice(5);
      var ci = rest.indexOf(':');
      return ci >= 0 ? rest.slice(0, ci) : rest;
    }
    function regroupTagFacetFilters(ff) {
      if (!Array.isArray(ff)) return ff;
      var out = [];
      ff.forEach(function (group) {
        // Only touch an OR group made entirely of `tags:` filters spanning >1 axis.
        if (Array.isArray(group) && group.length > 1 &&
            group.every(function (x) { return typeof x === 'string' && axisOf(x); })) {
          var byAxis = {};
          group.forEach(function (t) {
            var a = axisOf(t);
            (byAxis[a] = byAxis[a] || []).push(t);
          });
          var axes = Object.keys(byAxis);
          if (axes.length > 1) { axes.forEach(function (a) { out.push(byAxis[a]); }); return; }
        }
        out.push(group);
      });
      return out;
    }
    function mapRequests(requests) {
      if (!Array.isArray(requests)) return requests;
      return requests.map(function (r) {
        if (!r || !r.params) return r;
        var np = null; // lazily-cloned params
        // (1) regroup tag facetFilters by axis (AND across axes, OR within)
        if (r.params.facetFilters) {
          np = np || Object.assign({}, r.params);
          np.facetFilters = regroupTagFacetFilters(r.params.facetFilters);
        }
        // (2) $0 is quote-only ("call for price"), not a real price — keep it OUT
        // of the Price sorts entirely so it never leads "Price: Low to High".
        if (r.indexName === INDEX_PRICE_ASC || r.indexName === INDEX_PRICE_DESC) {
          np = np || Object.assign({}, r.params);
          var nf = Array.isArray(np.numericFilters) ? np.numericFilters.slice()
                 : (np.numericFilters ? [np.numericFilters] : []);
          if (nf.indexOf('price>0') < 0) nf.push('price>0');
          np.numericFilters = nf;
        }
        return np ? Object.assign({}, r, { params: np }) : r;
      });
    }
    // Override .search in place (preserves the client's prototype + other methods).
    // FAIL-OPEN: any Typesense error → replay the same requests against Algolia
    // direct (old replica index names via ALG_INDEX_MAP), so search never breaks.
    var searchClient = _baseClient;
    var _tsSearch = _baseClient.search.bind(_baseClient);
    var _algSearch = _algoliaFallback.search.bind(_algoliaFallback);
    function toAlgoliaRequests(requests) {
      return requests.map(function (r) {
        var p = r.params;
        // params.filters carries TYPESENSE syntax (see the collection-scope
        // block) — convert back to Algolia filter syntax for the fallback:
        //   `attr:!=[\`A\`,\`B\`]` → `NOT attr:"A" AND NOT attr:"B"`
        //   `attr:=value`          → `attr:value`
        //   ` && `                 → ` AND `
        if (p && p.filters && (p.filters.indexOf(':=') >= 0 || p.filters.indexOf(':!=[') >= 0)) {
          var alg = p.filters
            .replace(/([\w.]+):!=\[([^\]]*)\]/g, function (_m, attr, vals) {
              return vals.split(/`\s*,\s*`/).map(function (v) {
                return 'NOT ' + attr + ':"' + v.replace(/`/g, '') + '"';
              }).join(' AND ');
            })
            .replace(/([\w.]+):=/g, '$1:')
            .replace(/\s*&&\s*/g, ' AND ');
          p = Object.assign({}, p, { filters: alg });
        }
        return Object.assign({}, r, { indexName: ALG_INDEX_MAP[r.indexName] || r.indexName, params: p });
      });
    }
    searchClient.search = function (requests) {
      var mapped = mapRequests(requests);
      return _tsSearch(mapped).then(function (res) {
        // GA4: observe settled search term + hit count (no-op on browse/empty).
        try { if (window.KnobsGA4) window.KnobsGA4.observeResults(mapped, res); } catch (ge) {}
        return res;
      }).catch(function (e) {
        // GA4: log the fail-open through GTM (independent of Typesense).
        try { if (window.KnobsGA4) window.KnobsGA4.fallback({ surface: 'instantsearch', error: e }); } catch (ge) {}
        console.warn('[knobs-is] typesense failed, falling back to algolia', e);
        return _algSearch(toAlgoliaRequests(mapped));
      });
    };

    // ─── URL routing: knobs.co filter params ↔ InstantSearch state ───
    // IMPORTANT: these param names must NOT start with `filter.` — that prefix
    // collides with Shopify's NATIVE storefront filters, and Shopify's server
    // re-serializes bracketed multi-values (filter.p.tag[0]/[1]) into a broken
    // hash on reload, which we then can't parse. So we use our OWN names and
    // pack multi-values into ONE param joined by `~` (no array brackets → nothing
    // for Shopify to mangle → reload + share work).
    // URL shape:
    //   ?q=copper                                       (query)
    //   &page=2                                         (Shopify is 1-indexed; IS is 0-indexed)
    //   &sort_by=best-selling                           (also Shopify-native; single value, safe)
    //   &spotlight=Style: Modern~Finish-Class: Bronze   (finish/drill/style/material; ~ = multi)
    //   &by=Berenson~Top Knobs                          (brand / vendor)
    //   &kind=Cabinet Hardware - Knobs                  (product type)
    //   &budget=10-50                                   (price min-max; either side optional)
    var FILTER_DELIM = '~';
    function splitFilter(v) {
      return String(v).split(FILTER_DELIM).filter(Boolean);
    }

    var search = instantsearch({
      indexName: INDEX_PRIMARY,
      searchClient: searchClient,
      // Insights OFF for the Typesense engine: Algolia queryIDs don't exist here,
      // so the aa() bindings below no-op harmlessly (they check for queryID).
      // Search analytics events move to Typesense's analytics API post-cutover.
      insights: false,
      routing: {
        stateMapping: {
          // All state lives under INDEX_PRIMARY; sortBy is a property inside it that
          // points to a replica index when non-default. This is how InstantSearch v4
          // handles the SortBy widget switching indices.
          stateToRoute: function (uiState) {
            var s = uiState[INDEX_PRIMARY] || {};
            var refs = s.refinementList || {};
            var range = s.range || {};
            var route = {};
            if (s.query) route.q = s.query;
            if (s.page && s.page > 0) route.page = String(s.page + 1);
            if (s.sortBy === INDEX_BEST_SELLING) route.sort_by = 'best-selling';
            else if (s.sortBy === INDEX_PRICE_ASC) route.sort_by = 'price-ascending';
            else if (s.sortBy === INDEX_PRICE_DESC) route.sort_by = 'price-descending';
            else if (s.sortBy === INDEX_TITLE_ASC) route.sort_by = 'title-ascending';
            else if (s.sortBy === INDEX_TITLE_DESC) route.sort_by = 'title-descending';
            if (refs.vendor && refs.vendor.length) route.by = refs.vendor.join(FILTER_DELIM);
            if (refs.product_type && refs.product_type.length) route.kind = refs.product_type.join(FILTER_DELIM);
            if (refs.tags && refs.tags.length) route.spotlight = refs.tags.join(FILTER_DELIM);
            if (refs['meta.details.finish'] && refs['meta.details.finish'].length) route.finish = refs['meta.details.finish'].join(FILTER_DELIM);
            if (range.price) {
              var parts = range.price.split(':');
              if (parts[0] || parts[1]) route.budget = (parts[0] || '') + '-' + (parts[1] || '');
            }
            return route;
          },
          routeToState: function (routeState) {
            var state = {};
            var indexState = {
              query: routeState.q || '',
              page: routeState.page ? Math.max(0, parseInt(routeState.page, 10) - 1) : 0,
            };
            if (routeState.sort_by === 'best-selling') {
              indexState.sortBy = INDEX_BEST_SELLING;
            } else if (routeState.sort_by === 'price-ascending') {
              indexState.sortBy = INDEX_PRICE_ASC;
            } else if (routeState.sort_by === 'price-descending') {
              indexState.sortBy = INDEX_PRICE_DESC;
            } else if (routeState.sort_by === 'title-ascending') {
              indexState.sortBy = INDEX_TITLE_ASC;
            } else if (routeState.sort_by === 'title-descending') {
              indexState.sortBy = INDEX_TITLE_DESC;
            }
            var refList = {};
            if (routeState.by) refList.vendor = splitFilter(routeState.by);
            if (routeState.kind) refList.product_type = splitFilter(routeState.kind);
            if (routeState.spotlight) refList.tags = splitFilter(routeState.spotlight);
            if (routeState.finish) refList['meta.details.finish'] = splitFilter(routeState.finish);
            if (Object.keys(refList).length) indexState.refinementList = refList;
            if (routeState.budget) {
              var bp = String(routeState.budget).split('-');
              indexState.range = { price: (bp[0] || '') + ':' + (bp[1] || '') };
            }
            state[INDEX_PRIMARY] = indexState;
            return state;
          },
        },
      },
    });

    /* ───────── Virtual SearchBox — required for routing's query state to wire to helper ───────── */
    // The Knobs.co header already has its own (autocomplete-powered) search input;
    // we don't render a visible search box on /search. But InstantSearch needs a
    // SearchBox widget present to connect uiState.query → helper.query.
    // Using connectSearchBox with a no-op render gives us that wiring without DOM.
    var virtualSearchBox = connectors.connectSearchBox(function () {});
    search.addWidgets([virtualSearchBox({})]);

    /* ───────── Search parameters ─────────
       Page size applies to BOTH /search and collection browse, so it is seeded
       here — outside the KIS_IS_COLLECTION gate below. (Before 2026-07-10 the
       only configure() call lived inside that gate, so /search never sent a
       page size at all and silently ran at Typesense's default of 10.) */
    var _configureParams = { hitsPerPage: HITS_PER_PAGE };

    /* ───────── Collection scope (collection pages only) ─────────
       Constrain every query to this collection's members via the indexed
       collection_ids attribute. This is the only behavioral difference from
       /search; everything below (facets, hits, sort, pagination, insights)
       is shared. On /search KIS_IS_COLLECTION is false so this is skipped. */
    if (KIS_IS_COLLECTION) {
      // Exclusion clauses (furniture + organizers) come from the marker's
      // data-extra-filters; blank on furniture/organizer/bathroom collections.
      var _extra = KIS_COLL_MARKER ? (KIS_COLL_MARKER.getAttribute('data-extra-filters') || '') : '';
      // Collection scope rides on facetFilters (the adapter translates positive
      // entries natively). Exclusions CANNOT use Algolia's `-attr:value`
      // facetFilters negation — the adapter reads `-product_type` as a literal
      // field name and Typesense 400s (verified 2026-07-08: blanked every
      // collection with furniture-demote exclusions). Instead the exclusions go
      // through `params.filters`, which the adapter passes through VERBATIM into
      // filter_by — so we emit Typesense syntax here, and toAlgoliaRequests()
      // converts it back for the Algolia fail-open path.
      // BOTH the collection scope and the exclusions ride on the raw `filters`
      // param — NOT facetFilters. InstantSearch's helper serializes widget
      // refinements into facetFilters, clobbering anything configure() put
      // there (verified 2026-07-08: refining Brand dropped the collection
      // scope — 59 real results rendered as 553 sitewide ones). `filters` is a
      // separate channel that survives refinements — same contract the
      // original Algolia code relied on. Emitted in TYPESENSE syntax (the
      // adapter passes `filters` verbatim into filter_by); toAlgoliaRequests()
      // converts it back for the Algolia fail-open path.
      var _exclVals = [];
      var _exclRe = /NOT\s+product_type:"([^"]+)"/g, _exclM;
      while ((_exclM = _exclRe.exec(_extra))) {
        _exclVals.push(_exclM[1]);
      }
      var _clauses = [];
      if (KIS_COLLECTION_ID) {
        _clauses.push('collection_ids:=' + KIS_COLLECTION_ID);
      }
      if (_exclVals.length) {
        _clauses.push('product_type:!=[' + _exclVals.map(function (v) {
          return '`' + v + '`'; // backtick-escape: values contain spaces + commas
        }).join(',') + ']');
      }
      if (_clauses.length) {
        _configureParams.filters = _clauses.join(' && ');
      }
    }

    // Single configure() for both page types. Note PAGE_BRAND_CAP only reorders
    // (capByVendor never drops a hit), so it stays safe at any page size.
    search.addWidgets([widgets.configure(_configureParams)]);

    /* ───────── Widget: Stats ───────── */
    search.addWidgets([
      widgets.stats({
        container: '#is-stats',
        templates: {
          text: function (data) {
            var n = data.nbHits || 0;
            // '*' is the Typesense wildcard for empty-query browse — never show it
            var q = (data.query === '*' ? '' : data.query) || '';
            return 'Showing <strong>' + n + '</strong> result' + (n !== 1 ? 's' : '') +
                   (q ? ' for &ldquo;' + escapeHtml(q) + '&rdquo;' : '');
          },
        },
      }),
    ]);

    /* ───────── Widget: SortBy ───────── */
    search.addWidgets([
      widgets.sortBy({
        container: '#is-sort-by',
        items: [
          { label: 'Relevance', value: INDEX_PRIMARY },
          { label: 'Best Selling', value: INDEX_BEST_SELLING },
          { label: 'Price: Low to High', value: INDEX_PRICE_ASC },
          { label: 'Price: High to Low', value: INDEX_PRICE_DESC },
          { label: 'Alphabetical: A–Z', value: INDEX_TITLE_ASC },
          { label: 'Alphabetical: Z–A', value: INDEX_TITLE_DESC },
        ],
        cssClasses: { select: 'knobs-srch__sort-select' },
      }),
    ]);

    /* ───────── Widget: Current refinements (chips) ───────── */
    search.addWidgets([
      widgets.currentRefinements({
        container: '#is-current-refinements',
        // Friendly chip label for the Specific Finish facet (raw attribute
        // path would render as "meta.details.finish").
        transformItems: function (items) {
          return items.map(function (it) {
            return it.attribute === 'meta.details.finish'
              ? Object.assign({}, it, { label: 'Specific Finish' })
              : it;
          });
        },
        cssClasses: {
          root: 'knobs-srch__chips',
          list: 'knobs-srch__chips-list',
          item: 'knobs-srch__chip',
          label: 'knobs-srch__chip-label',
          delete: 'knobs-srch__chip-x',
        },
      }),
      widgets.clearRefinements({
        container: '#is-clear-refinements',
        templates: { resetLabel: 'Clear all' },
        cssClasses: { button: 'knobs-srch__clear-all' },
      }),
    ]);

    /* ───────── Widget: Price range — custom dual-handle slider (matches existing CSS) ───────── */
    var priceSliderRenderer = function (renderState, isFirstRender) {
      var range = renderState.range || {};
      var start = renderState.start || [];
      var refine = renderState.refine;
      var min = Math.floor(range.min || 0);
      var max = Math.ceil(range.max || 1000);
      if (max <= min) max = min + 1;
      var currentMin = (start[0] !== undefined && start[0] > -Infinity) ? Math.floor(start[0]) : min;
      var currentMax = (start[1] !== undefined && start[1] < Infinity) ? Math.ceil(start[1]) : max;

      var container = document.getElementById('is-price-range');
      if (!container) return;

      // Don't build until Algolia's real price stats are in. The first render(s)
      // carry an empty range (placeholder 0–1000); building then makes the slider
      // flash "$0–$1000" before it snaps to the true range. Wait for real data.
      var hasRealRange = range.min !== undefined && range.max !== undefined && max > min;
      var built = container.querySelector('.knobs-srch__price-slider');

      if (!built) {
        if (!hasRealRange) return;
        // Build the slider markup once
        container.innerHTML =
          '<div class="knobs-srch__price-slider" data-min="' + min + '" data-max="' + max + '">' +
            '<div class="knobs-srch__price-display">' +
              '<span class="knobs-srch__price-display-low">$' + currentMin + '</span>' +
              '<span class="knobs-srch__price-display-high">$' + currentMax + '</span>' +
            '</div>' +
            '<div class="knobs-srch__price-track-wrap">' +
              '<div class="knobs-srch__price-track"></div>' +
              '<div class="knobs-srch__price-fill"></div>' +
              '<input type="range" class="knobs-srch__price-thumb knobs-srch__price-thumb--low"  min="' + min + '" max="' + max + '" value="' + currentMin + '" step="1" aria-label="Minimum price">' +
              '<input type="range" class="knobs-srch__price-thumb knobs-srch__price-thumb--high" min="' + min + '" max="' + max + '" value="' + currentMax + '" step="1" aria-label="Maximum price">' +
            '</div>' +
            '<p class="knobs-srch__price-range-note">Range: $' + min + ' &ndash; $' + max + '</p>' +
          '</div>';

        var slider = container.querySelector('.knobs-srch__price-slider');
        var low = slider.querySelector('.knobs-srch__price-thumb--low');
        var high = slider.querySelector('.knobs-srch__price-thumb--high');
        var fill = slider.querySelector('.knobs-srch__price-fill');
        var lowDisp = slider.querySelector('.knobs-srch__price-display-low');
        var highDisp = slider.querySelector('.knobs-srch__price-display-high');

        // Read the live bounds off the inputs, not the closure's `min`/`max`.
        // Those closure values are captured on the FIRST render, when Algolia's
        // price stats aren't in yet (placeholder 0–1000). The real range arrives
        // on a later render and updates the input min/max attributes — so using
        // the stale closure span here produced a wildly out-of-range `right`
        // percentage (e.g. right: -2094%), bleeding the fill across the page.
        function updateFill() {
          var curMin = parseFloat(low.min);
          var curMax = parseFloat(low.max);
          var lv = parseInt(low.value, 10);
          var hv = parseInt(high.value, 10);
          if (lv > hv) { var tmp = lv; lv = hv; hv = tmp; }
          var span = curMax - curMin;
          var leftPct = span > 0 ? ((lv - curMin) / span) * 100 : 0;
          var rightPct = span > 0 ? 100 - ((hv - curMin) / span) * 100 : 0;
          // Clamp so a transient/stale range can never push the fill past the track.
          fill.style.left = Math.max(0, Math.min(100, leftPct)) + '%';
          fill.style.right = Math.max(0, Math.min(100, rightPct)) + '%';
          lowDisp.textContent = '$' + lv;
          highDisp.textContent = '$' + hv;
        }

        var refineTimeout;
        function commitRefine() {
          var curMin = parseFloat(low.min);
          var curMax = parseFloat(low.max);
          var lv = parseInt(low.value, 10);
          var hv = parseInt(high.value, 10);
          if (lv > hv) { var tmp = lv; lv = hv; hv = tmp; }
          clearTimeout(refineTimeout);
          refineTimeout = setTimeout(function () {
            refine([lv === curMin ? undefined : lv, hv === curMax ? undefined : hv]);
          }, 250);
        }

        low.addEventListener('input', updateFill);
        high.addEventListener('input', updateFill);
        low.addEventListener('change', commitRefine);
        high.addEventListener('change', commitRefine);
        updateFill();
      } else {
        // Subsequent renders: sync values from state without rebuilding
        var slider = container.querySelector('.knobs-srch__price-slider');
        var low = slider.querySelector('.knobs-srch__price-thumb--low');
        var high = slider.querySelector('.knobs-srch__price-thumb--high');
        // Update min/max if Algolia returned a different range
        low.min = min; low.max = max;
        high.min = min; high.max = max;
        // Only update values if user isn't currently interacting
        if (document.activeElement !== low && document.activeElement !== high) {
          low.value = currentMin;
          high.value = currentMax;
          var event = new Event('input');
          low.dispatchEvent(event);
        }
        // Update range note
        var note = slider.querySelector('.knobs-srch__price-range-note');
        if (note) note.innerHTML = 'Range: $' + min + ' &ndash; $' + max;
      }
    };

    var priceSliderWidget = connectors.connectRange(priceSliderRenderer);
    search.addWidgets([priceSliderWidget({ attribute: 'price' })]);

    /* ───────── Reusable: custom refinement-list renderer (matches existing markup) ───────── */
    function makeListRenderer(containerId, options) {
      options = options || {};
      var labelFn = options.labelFn || function (i) { return i.label; };
      return function (renderState, isFirstRender) {
        var items = renderState.items || [];
        var refine = renderState.refine;
        var container = document.getElementById(containerId);
        if (!container) return;
        if (options.transformItems) items = options.transformItems(items);
        container.innerHTML = '';
        var visible = items.slice(0, 6);
        var hidden = items.slice(6);
        function renderItem(item) {
          var label = document.createElement('label');
          label.className = 'knobs-srch__filter-item' + (item.isRefined ? ' is-active' : '');
          var cb = document.createElement('input');
          cb.type = 'checkbox';
          cb.checked = !!item.isRefined;
          cb.addEventListener('change', function () { refine(item.value); });
          label.appendChild(cb);
          var text = document.createElement('span');
          text.className = 'knobs-srch__filter-text';
          text.textContent = labelFn(item);
          label.appendChild(text);
          var count = document.createElement('span');
          count.className = 'knobs-srch__filter-count';
          count.textContent = '(' + item.count + ')';
          label.appendChild(count);
          return label;
        }
        visible.forEach(function (item) { container.appendChild(renderItem(item)); });
        if (hidden.length > 0) {
          var hiddenWrap = document.createElement('div');
          hiddenWrap.className = 'knobs-srch__filter-overflow';
          hiddenWrap.style.display = 'none';
          hidden.forEach(function (item) { hiddenWrap.appendChild(renderItem(item)); });
          container.appendChild(hiddenWrap);
          var more = document.createElement('button');
          more.type = 'button';
          more.className = 'knobs-srch__filter-more';
          more.textContent = 'See ' + hidden.length + ' more';
          more.addEventListener('click', function () {
            var open = hiddenWrap.style.display !== 'none';
            hiddenWrap.style.display = open ? 'none' : 'block';
            more.textContent = open ? 'See ' + hidden.length + ' more' : 'See less';
          });
          container.appendChild(more);
        }
      };
    }

    /* ───────── Widget: Vendor (Brand) ───────── */
    var vendorWidget = connectors.connectRefinementList(makeListRenderer('is-vendor'));
    search.addWidgets([
      vendorWidget({
        attribute: 'vendor', operator: 'or', limit: 50, sortBy: ['count:desc', 'name:asc'],
        // Display order = count × brand weight, so devalued brands (Alno, etc.)
        // drop without flipping everyone else off count-order.
        transformItems: function (items) {
          return items.slice().sort(function (a, b) {
            var wa = a.count * brandWeight(a.label), wb = b.count * brandWeight(b.label);
            return wb !== wa ? wb - wa : a.label.localeCompare(b.label);
          });
        },
      }),
    ]);

    /* ───────── Widget: Product type ───────── */
    // Strip category prefix from labels: "Cabinet Hardware - Knobs" → "Knobs",
    // "Bathroom - Towel Bars" → "Towel Bars". The full string is still the
    // refinement value (so filtering still works); only the displayed label changes.
    function productTypeLabel(item) {
      var lbl = item.label || '';
      var dash = lbl.indexOf(' - ');
      if (dash > 0) return lbl.slice(dash + 3).trim();
      return lbl;
    }
    var productTypeWidget = connectors.connectRefinementList(makeListRenderer('is-product-type', { labelFn: productTypeLabel }));
    search.addWidgets([
      productTypeWidget({ attribute: 'product_type', operator: 'or', limit: 30, sortBy: ['count:desc', 'name:asc'] }),
    ]);

    /* ───────── Custom widget: Tag-axis facets (one connector, multi-section render) ───────── */
    var tagAxisRenderer = function (renderState, isFirstRender) {
      var items = renderState.items || [];
      var refine = renderState.refine;

      var groups = groupTagItems(items);
      var ordered = orderedGroupKeys(groups);

      var container = document.getElementById('is-tag-axes');
      if (!container) return;
      // Rescue the Specific Finish group before the wipe (it gets re-inserted
      // right after the Finish group below; its widget renders independently).
      var sfg = sfGroupEl();
      if (sfg && sfg.parentNode) sfg.parentNode.removeChild(sfg);
      container.innerHTML = '';

      ordered.forEach(function (prefix) {
        var groupItems = groups[prefix].slice().sort(function (a, b) { return b.count - a.count; });
        var displayLabel = displayGroupLabel(prefix);
        var isFinishGroup = /finish|colou?r/i.test(prefix);

        var details = document.createElement('details');
        details.className = 'knobs-srch__filter-group';
        details.open = true;

        var summary = document.createElement('summary');
        summary.className = 'knobs-srch__filter-label';
        summary.innerHTML = '<span>' + escapeHtml(displayLabel) + '</span>' +
                            '<span class="knobs-srch__filter-chev" aria-hidden="true">+</span>';
        details.appendChild(summary);

        var body = document.createElement('div');
        body.className = 'knobs-srch__filter-body';

        var visible = groupItems.slice(0, 6);
        var hidden = groupItems.slice(6);

        function renderItem(item) {
          var label = document.createElement('label');
          label.className = 'knobs-srch__filter-item' + (item.isRefined ? ' is-active' : '');
          var cb = document.createElement('input');
          cb.type = 'checkbox';
          cb.checked = !!item.isRefined;
          cb.value = item.value;
          cb.addEventListener('change', function () { refine(item.value); });
          label.appendChild(cb);
          if (isFinishGroup) {
            // Finish swatch — inline-styled so it can't depend on / break any CSS.
            var sw = document.createElement('span');
            sw.className = 'knobs-swatch knobs-srch__filter-swatch';
            sw.setAttribute('aria-hidden', 'true');
            sw.setAttribute('style', finishSwatchStyle(item.displayValue) + ';width:14px;height:14px;border-radius:50%;display:inline-block;margin-right:8px;flex:0 0 auto;border:1px solid rgba(0,0,0,0.15)');
            label.appendChild(sw);
          }
          var text = document.createElement('span');
          text.className = 'knobs-srch__filter-text';
          text.textContent = item.displayValue;
          label.appendChild(text);
          var count = document.createElement('span');
          count.className = 'knobs-srch__filter-count';
          count.textContent = '(' + item.count + ')';
          label.appendChild(count);
          return label;
        }

        visible.forEach(function (item) { body.appendChild(renderItem(item)); });

        if (hidden.length > 0) {
          var hiddenWrap = document.createElement('div');
          hiddenWrap.className = 'knobs-srch__filter-overflow';
          hiddenWrap.style.display = 'none';
          hidden.forEach(function (item) { hiddenWrap.appendChild(renderItem(item)); });
          body.appendChild(hiddenWrap);

          var more = document.createElement('button');
          more.type = 'button';
          more.className = 'knobs-srch__filter-more';
          more.textContent = 'See ' + hidden.length + ' more';
          more.addEventListener('click', function () {
            var open = hiddenWrap.style.display !== 'none';
            hiddenWrap.style.display = open ? 'none' : 'block';
            more.textContent = open ? 'See ' + hidden.length + ' more' : 'See less';
          });
          body.appendChild(more);
        }

        details.appendChild(body);
        container.appendChild(details);

        // Slot the Specific Finish drill-down directly under the Finish group.
        if (sfg && prefix === 'Finish-Class') container.appendChild(sfg);
      });
    };

    var customTagAxisWidget = connectors.connectRefinementList(tagAxisRenderer);
    search.addWidgets([
      customTagAxisWidget({ attribute: 'tags', operator: 'or', limit: 1000 }),
    ]);

    /* ───────── Custom widget: Specific Finish (meta.details.finish) ───────── */
    // Visible only while a Finish family is active (checked family or a
    // /collections/finish-<family> page); values family-filtered client-side
    // (see familiesOfFinish). Refined values always stay listed so they can
    // be unchecked even after their family is.
    var specificFinishRenderer = function (renderState) {
      var group = sfGroupEl();
      if (!group) return;
      var container = group.querySelector('#is-specific-finish');
      if (!container) return;
      var refine = renderState.refine;
      var items = renderState.items || [];

      var classes = activeFinishClasses(renderState);
      var shown;
      if (classes.length) {
        shown = items.filter(function (it) {
          if (it.isRefined) return true;
          var fams = familiesOfFinish(it.label);
          for (var j = 0; j < fams.length; j++) {
            if (classes.indexOf(fams[j]) >= 0) return true;
          }
          return false;
        });
      } else {
        shown = items.filter(function (it) { return it.isRefined; });
      }

      if (!shown.length) { group.style.display = 'none'; return; }
      group.style.display = '';
      container.innerHTML = '';

      function renderItem(item) {
        var label = document.createElement('label');
        label.className = 'knobs-srch__filter-item' + (item.isRefined ? ' is-active' : '');
        var cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.checked = !!item.isRefined;
        cb.value = item.value;
        cb.addEventListener('change', function () { refine(item.value); });
        label.appendChild(cb);
        var sw = document.createElement('span');
        sw.className = 'knobs-swatch knobs-srch__filter-swatch';
        sw.setAttribute('aria-hidden', 'true');
        sw.setAttribute('style', finishSwatchStyle(item.label) + ';width:14px;height:14px;border-radius:50%;display:inline-block;margin-right:8px;flex:0 0 auto;border:1px solid rgba(0,0,0,0.15)');
        label.appendChild(sw);
        var text = document.createElement('span');
        text.className = 'knobs-srch__filter-text';
        text.textContent = item.label;
        label.appendChild(text);
        var count = document.createElement('span');
        count.className = 'knobs-srch__filter-count';
        count.textContent = '(' + item.count + ')';
        label.appendChild(count);
        return label;
      }

      var visible = shown.slice(0, 6);
      var hidden = shown.slice(6);
      visible.forEach(function (item) { container.appendChild(renderItem(item)); });
      if (hidden.length > 0) {
        var hiddenWrap = document.createElement('div');
        hiddenWrap.className = 'knobs-srch__filter-overflow';
        hiddenWrap.style.display = 'none';
        hidden.forEach(function (item) { hiddenWrap.appendChild(renderItem(item)); });
        container.appendChild(hiddenWrap);
        var more = document.createElement('button');
        more.type = 'button';
        more.className = 'knobs-srch__filter-more';
        more.textContent = 'See ' + hidden.length + ' more';
        more.addEventListener('click', function () {
          var open = hiddenWrap.style.display !== 'none';
          hiddenWrap.style.display = open ? 'none' : 'block';
          more.textContent = open ? 'See ' + hidden.length + ' more' : 'See less';
        });
        container.appendChild(more);
      }
    };

    var specificFinishWidget = connectors.connectRefinementList(specificFinishRenderer);
    search.addWidgets([
      specificFinishWidget({ attribute: 'meta.details.finish', operator: 'or', limit: 300, sortBy: ['count:desc', 'name:asc'] }),
    ]);

    /* ───────── Widget: Hits ───────── */
    search.addWidgets([
      widgets.hits({
        container: '#is-hits',
        // Collection browse only: cap one brand's run at the front of the page so
        // it can't wall page 1. No-op on /search (query relevance left intact).
        transformItems: function (items) {
          // SSR-first: until the shopper actually refines (filter/sort/page), the
          // curated SSR grid is what's shown — so render ZERO Algolia cards here so
          // the hidden #is-hits loads no images. Once refined, render the real
          // (brand-capped) results, which then replace the SSR grid.
          if (KIS_IS_COLLECTION && !isRefined()) return [];
          return KIS_IS_COLLECTION ? capByVendor(items, PAGE_BRAND_CAP) : items;
        },
        cssClasses: {
          root: 'knobs-srch__grid-wrap',
          list: 'knobs-srch__grid',
          item: 'knobs-srch__card',
        },
        templates: {
          // Function template: detect visualizer-intent queries and always offer
          // the Cabinet Hardware Visualizer as a helpful fallback on zero results.
          empty: function (results) {
            var q = (results && results.query) ? String(results.query) : '';
            var esc = q.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            var wantsViz = /visuali[sz]|preview|on (my|your) cabinet|see .*(cabinet|knob|pull|hardware)/i.test(q);
            var viz =
              '<a class="knobs-srch__viz-card" href="/pages/cabinet-hardware-visualizer">' +
                '<span class="knobs-srch__viz-icon" aria-hidden="true">▣</span>' +
                '<span class="knobs-srch__viz-copy">' +
                  '<strong>Try the Cabinet Hardware Visualizer</strong>' +
                  '<span>See any knob or pull on a photo of your own cabinets &mdash; free, no login.</span>' +
                '</span>' +
              '</a>';
            if (wantsViz) {
              return '<div class="knobs-srch__empty">' +
                '<p>Looking for our visualizer? It’s right here:</p>' + viz + '</div>';
            }
            return '<div class="knobs-srch__empty">' +
              '<p>No results for &ldquo;' + esc + '&rdquo;. Try a different term or browse ' +
              '<a href="/collections/all">all products</a>.</p>' + viz + '</div>';
          },
          item: function (hit, bindEvent) {
            return renderHit(hit, {
              queryID: hit.__queryID,
              position: hit.__position,
              activeFinishClass: currentFinishClass,
            });
          },
        },
      }),
    ]);

    /* ───────── Widget: Pagination ───────── */
    search.addWidgets([
      widgets.pagination({
        container: '#is-pagination',
        padding: 2,
        cssClasses: {
          root: 'knobs-srch__pagination',
          list: 'knobs-srch__pagination-list',
          item: 'knobs-srch__pagination-item',
          selectedItem: 'is-active',
          link: 'knobs-srch__pagination-link',
        },
        showFirst: false,
        showLast: false,
      }),
    ]);

    /* ───────── Lifecycle ───────── */
    // renderedOnce is module-level (declared at the top of the IIFE) so the
    // failover timeout above can observe it.
    var lastStateSig = null;
    function getStateSig() {
      try {
        var state = search.getUiState ? search.getUiState() : {};
        var s = state[INDEX_PRIMARY] || state[INDEX_BEST_SELLING] || {};
        return JSON.stringify({
          q: s.query || '',
          page: s.page || 0,
          ref: s.refinementList || {},
          range: s.range || {},
          sort: state[INDEX_BEST_SELLING] ? 'bs' : 'rel',
        });
      } catch (e) { return ''; }
    }

    // SSR-first: is any real refinement active (filter / price / sort / page)?
    // When false on a collection page, the curated SSR grid stays visible; when
    // true, Algolia results take over (#is-hits). Query is always '' on collections.
    // A non-default sort switches the active index away from INDEX_PRIMARY, which
    // is itself treated as "refined".
    function isRefined() {
      try {
        var state = search.getUiState ? search.getUiState() : {};
        var keys = Object.keys(state);
        for (var i = 0; i < keys.length; i++) {
          var idx = keys[i];
          if (idx !== INDEX_PRIMARY) return true; // non-default sort index active
          var s = state[idx] || {};
          if (s.query) return true;
          if (s.page && s.page > 0) return true;
          if (s.sortBy && s.sortBy !== INDEX_PRIMARY) return true;
          if (s.range && Object.keys(s.range).length) return true;
          var rl = s.refinementList || {};
          for (var k in rl) { if (rl[k] && rl[k].length) return true; }
        }
        return false;
      } catch (e) { return false; }
    }

    // Update currentFinishClass from active tag refinements. Used by renderHit
    // to switch card images when a single Finish-Class is selected.
    function updateActiveFinishClass() {
      try {
        var state = search.getUiState ? search.getUiState() : {};
        var s = state[INDEX_PRIMARY] || state[INDEX_BEST_SELLING] || {};
        var tags = (s.refinementList && s.refinementList.tags) || [];
        var fcTags = tags.filter(function (t) { return /^Finish-Class:\s*/.test(t); });
        if (fcTags.length === 1) {
          currentFinishClass = fcTags[0].replace(/^Finish-Class:\s*/, '').trim().toLowerCase();
        } else {
          currentFinishClass = null;
        }
      } catch (e) {
        currentFinishClass = null;
      }
    }

    search.on('render', function () {
      updateActiveFinishClass();
      // SSR-first: the SSR grid stays until the shopper refines. Toggle the
      // grid-swap class on every render based on whether a refinement is active.
      if (KIS_IS_COLLECTION) {
        document.documentElement.classList.toggle('knobs-is-results', isRefined());
      }
      if (!renderedOnce) {
        renderedOnce = true;
        window.tkAlgoliaActive = true;  // ONLY after first successful render
        var fb = document.getElementById('is-loading-fallback');
        if (fb) fb.style.display = 'none';
        document.documentElement.classList.add('knobs-is-active');
        lastStateSig = getStateSig();
      } else {
        // Scroll to top of results when filter/sort/page changes
        var sig = getStateSig();
        if (sig !== lastStateSig) {
          var anchor = document.querySelector('.knobs-srch__main') || document.querySelector('.knobs-coll__main') || document.querySelector('.knobs-srch__layout');
          if (anchor) {
            var topY = anchor.getBoundingClientRect().top + window.pageYOffset - 100;
            // Only scroll up — don't yank user down if they're above the results
            if (window.pageYOffset > topY) {
              window.scrollTo({ top: Math.max(0, topY), behavior: 'smooth' });
            }
          }
          lastStateSig = sig;
        }
      }
      // Insights: click bindings on hits
      document.querySelectorAll('.knobs-pcard__link[data-object-id]').forEach(function (a) {
        if (a.__bound) return;
        a.__bound = true;
        a.addEventListener('click', function () {
          emitInsights('clickedObjectIDsAfterSearch', {
            userToken: (window.aa && window.aa.getUserToken && window.aa.getUserToken()) || undefined,
            index: INDEX_PRIMARY,
            eventName: 'PDP click from search',
            queryID: a.getAttribute('data-query-id'),
            objectIDs: [a.getAttribute('data-object-id')],
            positions: [parseInt(a.getAttribute('data-position'), 10) || 1],
          });
          // Remember queryID->objectID so the global /cart/add.js observer in
          // knobs-search-insights.js can fire the conversion event on add-to-cart.
          if (window.KnobsInsights) {
            window.KnobsInsights.store(a.getAttribute('data-object-id'), a.getAttribute('data-query-id'));
          }
          // GA4: select_item — the search→product attribution path (queryID-free).
          try {
            if (window.KnobsGA4) {
              var _card = a.closest('.knobs-pcard');
              var _nameEl = _card && _card.querySelector('.knobs-pcard__name, .knobs-pcard__title, [class*="__name"]');
              var _imgEl = _card && _card.querySelector('img[alt]');
              var _title = a.getAttribute('data-title') ||
                (_nameEl && _nameEl.textContent) ||
                (_imgEl && _imgEl.getAttribute('alt')) || '';
              window.KnobsGA4.selectItem({
                surface: 'instantsearch',
                objectID: a.getAttribute('data-object-id'),
                position: a.getAttribute('data-position'),
                title: (_title || '').trim() || undefined
              });
            }
          } catch (ge) {}
        });
      });
    });

    search.on('error', function (err) {
      console.warn('[knobs-is] error', err);
      // Leave current rendered state; don't disable native fallback if we never rendered.
    });

    try {
      search.start();
      window.__knobsSearch = search;  // expose for debugging
    } catch (e) {
      console.error('[knobs-is] start failed', e);
    }

    // Quick Add for Algolia-rendered cards. The theme's cart-drawer snippet binds
    // .knobs-pcard__quick-add buttons that exist at PAGE LOAD; Algolia hits render
    // later, so they need their own binding. Delegate on #is-hits ONLY (SSR cards
    // live outside it) so we never double-bind a button the cart drawer owns.
    var hitsEl = document.getElementById('is-hits');
    if (hitsEl) {
      hitsEl.addEventListener('click', function (e) {
        var btn = e.target.closest('.knobs-pcard__quick-add');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId || btn.classList.contains('is-adding')) return;
        btn.classList.add('is-adding');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        }).then(function (r) { return r.json(); }).then(function () {
          document.dispatchEvent(new CustomEvent('tk:cart:added'));
        }).catch(function () {}).then(function () {
          btn.classList.remove('is-adding');
        });
      });
    }
  }

  whenReady(startInstantSearch);
})();
